// CSV export for Patient Reports.
//
// Exports the CURRENT FILTERED SET across every page, not the rows on screen.
// That is the whole point of the button: with 5,000+ pages, exporting one
// page of 20 would be useless.
//
// CSV rather than PDF: this is tabular billing data that people open in Excel
// to pivot and total. A 100k-row PDF would be unreadable and enormous.
//
// One row per SERVICE (not per visit), because that is the grain the API
// returns and the grain finance works in — a visit with three tests becomes
// three rows sharing a visit number.

// Excel decides a field's type from its content. A value like `1-2` becomes a
// date, and a long UHID becomes scientific notation. Quoting every field and
// escaping embedded quotes is the usual defence.
const cell = (v) => {
  const s = v === undefined || v === null ? '' : String(v);
  return `"${s.replace(/"/g, '""')}"`;
};

const COLUMNS = [
  ['Visit No',    (r) => r.VisitNo],
  ['Bill No',     (r) => r.BILLNO],
  ['Bill Date',   (r) => r.billdate],
  ['UHID',        (r) => r.UHID],
  ['Patient',     (r) => r.PATIENTNAME],
  ['Age',         (r) => r.Age],
  ['Gender',      (r) => r.Gender],
  ['Test',        (r) => r.SERVICENAME],
  ['Department',  (r) => r.DEPARTMENT],
  ['Status',      (r) => r.Status],
  ['Referring Doctor', (r) => r.REFERALDOCTOR],
  ['Lab',         (r) => r.TaggedLabName || r.PCCName],
  ['City',        (r) => r.City],
  ['Net Amount',  (r) => r.NetAmt],
  ['Discount',    (r) => r.DiscountAmt],
];

export const rowsToCsv = (rows) => {
  const head = COLUMNS.map(([label]) => cell(label)).join(',');
  const body = rows.map((r) => COLUMNS.map(([, get]) => cell(get(r))).join(','));
  // ﻿ = BOM. Without it Excel on Windows reads UTF-8 as Latin-1 and
  // mangles any non-ASCII patient name.
  return `﻿${[head, ...body].join('\r\n')}`;
};

export const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking immediately can cancel the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Page through the API until everything matching the current filters is in
// hand. 200 is the server's MAX_PAGE_SIZE — asking for more is silently
// clamped, so this is the fewest possible round-trips.
export const EXPORT_PAGE_SIZE = 200;

export const fetchAllRows = async (fetchPage, baseArgs, onProgress) => {
  const out = [];
  for (let page = 1; page <= 500; page += 1) {   // hard stop: 100k rows
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchPage({ ...baseArgs, page, pageSize: EXPORT_PAGE_SIZE });
    const data = res?.data;
    if (!data) break;
    out.push(...(data.data || []));
    onProgress?.(out.length, data.meta?.totalMatched ?? 0);
    if (!data.meta?.hasNextPage) break;
  }
  return out;
};
